import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Input,
} from "reactstrap";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "components/Common/Breadcrumb";
import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import RsAdNewV2 from "components/buttons/Adnew-v2";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

const TASK_TYPES = {
  AGENT: 1,
  OFFICE: 2,
};

const typeLabel = type =>
  Number(type) === TASK_TYPES.OFFICE ? "Office Portal" : "Agent Portal";

const getTaskId = task =>
  task?.id || task?.agentOnBoardingTaskId || task?.agentOnboardingTaskId || null;

const normalizeList = response => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.resultData)) return response.data.resultData;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response)) return response;
  return [];
};

const emptyFormValues = {
  id: null,
  item: "",
  description: "",
  link: "",
  displayOrder: 1,
  type: TASK_TYPES.AGENT,
  isActive: true,
};

const validationSchema = Yup.object({
  item: Yup.string().trim().required("Task item is required"),
  description: Yup.string().trim().required("Description is required"),
  link: Yup.string().trim().url("Enter a valid URL").required("Link is required"),
  displayOrder: Yup.number()
    .typeError("Display order must be a number")
    .integer("Display order must be a whole number")
    .min(1, "Display order must be at least 1")
    .required("Display order is required"),
  type: Yup.number()
    .oneOf([TASK_TYPES.AGENT, TASK_TYPES.OFFICE], "Select a portal type")
    .required("Type is required"),
  isActive: Yup.boolean(),
});

/**
 * On Boarding tasks — Agent portal (type 1) / Office portal (type 2)
 * POST/PUT/DELETE /api/AgentOnBoardingTask
 */
const OnBoarding = props => {
  document.title = "On Boarding | Realty Space";

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formValues, setFormValues] = useState(emptyFormValues);

  const toggle = () => setIsOpen(prev => !prev);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      let response;
      try {
        response = await apiGet("AgentOnBoardingTask/all");
      } catch {
        response = await apiGet("AgentOnBoardingTask");
      }
      setTasks(normalizeList(response));
    } catch (error) {
      console.error("Error fetching onboarding tasks:", error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const sortedTasks = useMemo(
    () =>
      [...tasks].sort(
        (a, b) =>
          (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0)
      ),
    [tasks]
  );

  const closePanel = () => {
    setIsOpen(false);
    setIsEdit(false);
    setFormValues(emptyFormValues);
  };

  const openAddNew = e => {
    if (e?.preventDefault) e.preventDefault();
    setIsEdit(false);
    setFormValues(emptyFormValues);
    setIsOpen(true);
  };

  const openEdit = (e, task) => {
    if (e?.preventDefault) e.preventDefault();
    setIsEdit(true);
    setFormValues({
      id: getTaskId(task),
      item: task.item || "",
      description: task.description || "",
      link: task.link || "",
      displayOrder: Number(task.displayOrder) || 1,
      type: Number(task.type) || TASK_TYPES.AGENT,
      isActive: task.isActive !== false,
    });
    setIsOpen(true);
  };

  const buildPayload = values => {
    const payload = {
      item: values.item.trim(),
      description: values.description.trim(),
      link: values.link.trim(),
      displayOrder: Number(values.displayOrder) || 1,
      type: Number(values.type),
      isActive: Boolean(values.isActive),
    };
    if (isEdit && values.id) {
      payload.id = values.id;
    }
    return payload;
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = buildPayload(values);

    try {
      toast.loading(isEdit ? "Updating task..." : "Creating task...");
      if (isEdit) {
        await apiPut("AgentOnBoardingTask", payload);
      } else {
        await apiPost("AgentOnBoardingTask", payload);
      }
      toast.dismiss();
      toast.success(
        isEdit ? "Onboarding task updated" : "Onboarding task created"
      );
      resetForm({ values: emptyFormValues });
      closePanel();
      fetchTasks();
    } catch (error) {
      console.error("Error saving onboarding task:", error);
      toast.dismiss();
      toast.error(
        error?.response?.data?.message ||
          (isEdit
            ? "Failed to update onboarding task"
            : "Failed to create onboarding task")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (e, task) => {
    if (e?.preventDefault) e.preventDefault();
    const id = getTaskId(task);
    if (!id) {
      toast.error("Unable to delete: missing task id");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "This onboarding task will be deleted permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(async result => {
      if (!result.isConfirmed) return;

      try {
        toast.loading("Deleting task...");
        await apiDelete(`AgentOnBoardingTask/${id}`);
        toast.dismiss();
        toast.success("Onboarding task deleted");
        fetchTasks();
      } catch (error) {
        console.error("Error deleting onboarding task:", error);
        toast.dismiss();
        toast.error(
          error?.response?.data?.message || "Failed to delete onboarding task"
        );
      }
    });
  };

  return (
    <React.Fragment>
      <ToastContainer autoClose={2000} />
      <div className="page-content">
        <Container fluid>
          <Row className="mb20 align-items-center">
            <Col sm="7">
              <Breadcrumbs
                title={props.t("On Boarding")}
                breadcrumbItem={props.t("On Boarding")}
              />
            </Col>
            <Col sm="5">
              <div className="mt-3 float-end">
                <RsAdNewV2 onClick={openAddNew}>{props.t("Add New")}</RsAdNewV2>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {loading ? (
                    <p className="text-muted mb-0">Loading…</p>
                  ) : sortedTasks.length === 0 ? (
                    <p className="text-muted mb-0">
                      No onboarding tasks yet. Click <strong>Add New</strong> to
                      create one.
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle mb-0">
                        <thead>
                          <tr>
                           
                            <th style={{ width: 90 }}>Order</th>
                            <th style={{ width: 140 }}>Type</th>
                            <th>Item</th>
                         
                           
                            <th style={{ width: 100 }}>Status</th>
                            <th style={{ width: 200 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTasks.map((task, index) => (
                            <tr key={getTaskId(task) || index}>
                           
                              <td>{task.displayOrder}</td>
                              <td>{typeLabel(task.type)}</td>
                              <td>{task.item}</td>
                         
                             
                              <td>
                                {task.isActive ? (
                                  <span className="badge bg-success">Active</span>
                                ) : (
                                  <span className="badge bg-secondary">
                                    Inactive
                                  </span>
                                )}
                              </td>
                              <td>
                                <ActionEdit onClick={e => openEdit(e, task)} />{" "}
                                <ActionDelete
                                  onClick={e => handleDelete(e, task)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas
        isOpen={isOpen}
        toggle={() => (isOpen ? closePanel() : toggle())}
        direction="end"
      >
        <OffcanvasHeader toggle={closePanel}>
          {isEdit
            ? props.t("Edit On Boarding Task")
            : props.t("Add On Boarding Task")}
        </OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={formValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, setFieldValue, isSubmitting }) => (
              <Form>
                <FormGroup className="mb-3">
                  <Label>Portal Type *</Label>
                  <Input
                    type="select"
                    name="type"
                    value={values.type}
                    onChange={e =>
                      setFieldValue("type", Number(e.target.value))
                    }
                    className={
                      errors.type && touched.type ? "is-invalid" : ""
                    }
                  >
                    <option value={TASK_TYPES.AGENT}>Agent Portal</option>
                    <option value={TASK_TYPES.OFFICE}>Office Portal</option>
                  </Input>
                  <ErrorMessage
                    name="type"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Item *</Label>
                  <SafeField
                    name="item"
                    className={`form-control ${
                      errors.item && touched.item ? "is-invalid" : ""
                    }`}
                  />
                  <ErrorMessage
                    name="item"
                    component="div"
                    className="invalid-feedback"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Description *</Label>
                  <SafeField
                    name="description"
                    as="textarea"
                    rows="3"
                    className={`form-control ${
                      errors.description && touched.description
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="invalid-feedback"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Link *</Label>
                  <SafeField
                    name="link"
                    className={`form-control ${
                      errors.link && touched.link ? "is-invalid" : ""
                    }`}
                    placeholder="https://app.realtyspacehub.com/profile"
                  />
                  <ErrorMessage
                    name="link"
                    component="div"
                    className="invalid-feedback"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Display Order *</Label>
                  <SafeField
                    name="displayOrder"
                    type="number"
                    className={`form-control ${
                      errors.displayOrder && touched.displayOrder
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  <ErrorMessage
                    name="displayOrder"
                    component="div"
                    className="invalid-feedback"
                  />
                </FormGroup>

                <FormGroup check className="mb-4">
                  <Input
                    type="checkbox"
                    id="isActive"
                    checked={Boolean(values.isActive)}
                    onChange={e => setFieldValue("isActive", e.target.checked)}
                  />
                  <Label check htmlFor="isActive" className="ms-2">
                    Active
                  </Label>
                </FormGroup>

                <div className="d-flex gap-2">
                  <Button type="submit" color="primary" disabled={isSubmitting}>
                    {isSubmitting ? "Saving…" : isEdit ? "Update" : "Save"}
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    onClick={closePanel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </React.Fragment>
  );
};

OnBoarding.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(OnBoarding);
